import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import useSWR, { mutate } from "swr";

import dayjs from "dayjs";
import { Print } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Stack,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import PricingReportCsvExport from "./PricingReportCsvExport";
import { InsightsErrorState } from "../../components/Insights/InsightsAsyncState";
import AggregateResultsHeader from "./AggregateResultsHeader";
import "./design/design.scss";
import { getProvinceOptionList, toProv } from "../../components/Analytics/PriceReports/provinceUtils";
import { canViewPricingReports } from "../../common/utils/pricingReportsAccess";
import { insightsSectionClassName } from "../../common/utils/insightsDesignEnabled";
import {
  beefAiConfigured,
  fetchTimeSeriesAverageByProduct,
  fetchMonthlyAverageByProvince,
  fetchMonthlyAverageByRetailer,
  fetchProductAnalyticsRetailerSnapshot,
  fetchProductAnalyticsAverageUnitPriceByProvince,
  fetchQuarterlyAverageByProduct,
  fetchQuarterlyProductSummaryStats,
  fetchDistinctCounts,
  fetchAllPriceHistoryAggregates,
  fetchAllPriceHistoryRecords,
  fetchPriceHistoryAggregates,
  fetchPriceHistoryAggregateGroupCount,
  DEFAULT_PRICE_HISTORY_AGGREGATE_SORT,
  fetchPriceHistoryBreakdown,
  fetchAggregateGroupCoverImagesForPage,
  buildAggregateGroupRowKey,
  fetchProductsByIds,
  fetchPublishedProducts,
  updatePriceHistoryRecord,
  voidPriceHistoryRecord,
} from "../../services/beefAiPricing";
import {
  mapAggregateRowsToPriceReportRows,
  mapPriceHistoryRecordsToRawExportRows,
  normalizeAggregateProductId,
} from "./pricingReportUtils";
import {
  aggregateTableSignedHeaderIdFromUiState,
  aggregateTableSortParamFromSignedHeaderId,
  aggregateTableSortUiStateFromSortParam,
} from "./pricingReportAggregateSort";
import {
  buildQuarterlySingleSeriesForLookback,
  buildTimeBucketCandlestickSeries,
  buildTimeBucketFacetLineSeries,
  buildTimeBucketSmoothedCombinedLineSeries,
  computeAvailableQuarterlyLookbackYears,
  QUARTERLY_LOOKBACK_MAX_YEARS,
  shouldUseWeeklyTimeBuckets,
} from "./pricingChartsUtils";
import {
  AGGREGATE_TABLE_COLUMNS,
  formatAggregateCurrency,
  formatAggregateNetWeightKg,
  formatAggregateTotalWeight,
  DEFAULT_AGGREGATE_GRID_PAGE_SIZE,
  DEFAULT_AGGREGATE_PAGE_SIZE,
  DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE,
  DEFAULT_PRICING_REPORT_UNIT,
  formatProviderOptionLabel,
  formatProductOptionLabel,
  PRICING_CSV_COLUMNS,
  PRICING_RAW_CSV_COLUMNS,
  PRICING_REPORT_RESULTS_VIEW_SESSION_KEY,
  statisticCardContainerSx,
} from "./constants";
import PricingReportFilters from "./PricingReportFilters";
import {
  formatSelectedProductsChartLabel,
  formatSelectedProductsPrintLabel,
  formatSelectedRetailersPrintLabel,
  productIdsForPriceHistoryFilters,
  productPriceHistoryCountByIdFromAggregateRows,
  retailerPriceHistoryCountByNameFromAggregateRows,
  retailersForPriceHistoryFilters,
  soleSelectedProduct as getSoleSelectedProduct,
} from "./pricingReportProductFilter";
import PricingReportQuarterlySummarySection from "./PricingReportQuarterlySummarySection";
import PricingReportCharts from "./PricingReportCharts";
import ProductSnapshotPricingReportsBlock from "./ProductSnapshotPricingReportsBlock";
import ReportTable from "./ReportTable";
import PriceHistoryBreakdownDialog from "./PriceHistoryBreakdownDialog";
import PriceHistoryBreakdownGalleryDialog from "./PriceHistoryBreakdownGalleryDialog";
import PricingReportViewToggle from "./PricingReportViewToggle";
import PricingReportCardSortSelect from "./PricingReportCardSortSelect";
import PricingReportAggregateCardGrid from "./PricingReportAggregateCardGrid";
import PricingReportPrintView from "./PricingReportPrintView";
import { buildPricingReportPrintSnapshot } from "./buildPricingReportPrintSnapshot";
import {
  capturePricingReportChartImagesForPrint,
  waitForPricingReportChartCanvasesReady,
} from "./capturePricingReportChartImagesForPrint";
import { createAccordionCaptureEnteredWaiters } from "./createAccordionCaptureEnteredWaiters";
import { openPricingReportPrintDialog } from "./openPricingReportPrintDialog";
import { waitForElementImages } from "./waitForElementImages";
import { resizePricingReportChartsForPrint } from "./pricingReportChartResize";
import {
  pricingReportAllIncludedChartKeys,
  pricingReportIncludedChartKeys,
  pricingReportIncludedChartsReady,
  pricingReportIncludedSnapshotChartKeys,
  pricingReportPrintIsDisabled,
} from "./pricingReportPrintEligibility";

const AGGREGATE_RESULTS_TITLE = "Average unit price by month, province & retailer";
const AGGREGATE_RESULTS_SUBTITLE =
  "Aggregated average unit price grouped by month, province, and retailer for the current filters.";
const AGGREGATE_RESULTS_PRINT_TITLE =
  "Average unit price by <strong>month</strong>, <strong>province</strong> & <strong>retailer</strong>.";

const PRINT_AGGREGATE_TABLE_COLUMNS = AGGREGATE_TABLE_COLUMNS.filter(
  (column) => column.key !== "provider"
);

const CAPTURE_DELAY_BEFORE_PNG_MS = 150;

function readPricingReportResultsViewFromSession() {
  try {
    const stored = sessionStorage.getItem(PRICING_REPORT_RESULTS_VIEW_SESSION_KEY);
    if (stored === "table" || stored === "card") return stored;
  } catch (_) {
    /* sessionStorage unavailable */
  }
  return "table";
}

/** Pricing reports UI for use under Insights only (no standalone route). */
export function PricingReportsPanel() {
  const user = useSelector((state) => state.user);
  const retailer = useSelector((state) => state.retailer);
  const userMayViewPricingReports = canViewPricingReports(user);
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(4, "month").startOf("day"));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf("day"));
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProvidersInclude, setSelectedProvidersInclude] = useState(
    () => [...DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE]
  );
  const [aggregatePage, setAggregatePage] = useState(0);
  const [aggregateSort, setAggregateSort] = useState(DEFAULT_PRICE_HISTORY_AGGREGATE_SORT);
  const [aggregateRowsPerPage, setAggregateRowsPerPage] = useState(() =>
    readPricingReportResultsViewFromSession() === "card"
      ? DEFAULT_AGGREGATE_GRID_PAGE_SIZE
      : DEFAULT_AGGREGATE_PAGE_SIZE
  );
  const [aggregateDownloadError, setAggregateDownloadError] = useState("");
  const [selectedAggregateDrilldown, setSelectedAggregateDrilldown] = useState(null);
  const [breakdownPresentation, setBreakdownPresentation] = useState("table");
  const [galleryFocusedRecordId, setGalleryFocusedRecordId] = useState(null);
  const [galleryInitialPanelMode, setGalleryInitialPanelMode] = useState("view");
  const [pricingReportResultsView, setPricingReportResultsView] = useState(
    readPricingReportResultsViewFromSession
  );
  const [quarterlyLookbackYears, setQuarterlyLookbackYears] = useState(1);
  const [retailerChartCombinedView, setRetailerChartCombinedView] = useState(false);
  const [provinceChartCombinedView, setProvinceChartCombinedView] = useState(false);
  const [productChartCombinedView, setProductChartCombinedView] = useState(false);
  const [isDiscounted] = useState(false);
  const [pricingChartsAccordionExpanded, setPricingChartsAccordionExpanded] = useState(false);
  const [productSnapshotAccordionExpanded, setProductSnapshotAccordionExpanded] = useState(false);
  const [pricingReportPrintSnapshot, setPricingReportPrintSnapshot] = useState(null);
  const [isPricingPrintPreparing, setIsPricingPrintPreparing] = useState(false);
  const [pricingPrintError, setPricingPrintError] = useState("");

  const pricingReportChartRefsByKey = useRef({});
  const accordionCaptureWaitersRef = useRef(null);
  if (!accordionCaptureWaitersRef.current) {
    accordionCaptureWaitersRef.current = createAccordionCaptureEnteredWaiters();
  }
  const accordionCaptureWaiters = accordionCaptureWaitersRef.current;

  const selectedProductIds = useMemo(
    () => productIdsForPriceHistoryFilters(selectedProducts) ?? [],
    [selectedProducts]
  );
  const soleSelectedProduct = useMemo(
    () => getSoleSelectedProduct(selectedProducts),
    [selectedProducts]
  );
  const hasSelectedProducts = selectedProductIds.length > 0;

  const selectedRetailerNames = useMemo(
    () => retailersForPriceHistoryFilters(selectedRetailers) ?? [],
    [selectedRetailers]
  );

  const startDateForApi = dateFrom && dateFrom.isValid() ? dateFrom.format("YYYY-MM-DD") : "";
  const endDateForApi = dateTo && dateTo.isValid() ? dateTo.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : "";

  const distinctCountsDateRange = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
    }),
    [startDateForApi, endDateForApi]
  );

  const filterOptionsSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi
      ? ["pricing-filters", distinctCountsDateRange]
      : null;

  const { data: distinctProvinceRetailerCounts, error: filterOptionsError } = useSWR(
    filterOptionsSwrKey,
    async () => {
      const [provinceAggregateRows, retailerAggregateRows, providerAggregateRows] = await Promise.all([
        fetchDistinctCounts("province", distinctCountsDateRange),
        fetchDistinctCounts("retailer", distinctCountsDateRange),
        fetchDistinctCounts("provider", distinctCountsDateRange),
      ]);
      return {
        provinces: provinceAggregateRows,
        retailers: retailerAggregateRows,
        providers: providerAggregateRows,
      };
    },
    { revalidateOnFocus: false }
  );

  /** Same pipeline as `getProvinceOpts` / PriceFilters `provinceOpts`: codes from data → `getProvinceOptionList`. */
  const provinceCodeToBeefAiProvinceString = useMemo(() => {
    const map = new Map();
    for (const row of distinctProvinceRetailerCounts?.provinces || []) {
      const raw = row.province;
      const code = toProv(raw);
      if (code && raw != null && String(raw).trim() && !map.has(code)) {
        map.set(code, String(raw).trim());
      }
    }
    return map;
  }, [distinctProvinceRetailerCounts]);

  const provinceChoices = useMemo(() => {
    const codes = [
      ...new Set(
        (distinctProvinceRetailerCounts?.provinces || [])
          .map((row) => toProv(row.province))
          .filter(Boolean)
      ),
    ];
    return getProvinceOptionList(codes);
  }, [distinctProvinceRetailerCounts]);

  const provinceFilterForApi = useMemo(() => {
    if (selectedProvince == null) return undefined;
    return (
      provinceCodeToBeefAiProvinceString.get(selectedProvince.value) ?? selectedProvince.value
    );
  }, [selectedProvince, provinceCodeToBeefAiProvinceString]);

  const priceHistoryQueryFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      province: provinceFilterForApi,
      retailers: selectedRetailerNames.length > 0 ? selectedRetailerNames : undefined,
      productIds: selectedProductIds.length > 0 ? selectedProductIds : undefined,
      providersInclude: selectedProvidersInclude,
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted,
    }),
    [
      startDateForApi,
      endDateForApi,
      provinceFilterForApi,
      selectedRetailerNames,
      selectedProductIds,
      selectedProvidersInclude,
      isDiscounted,
    ]
  );

  const productSnapshotQueryFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      productIds: selectedProductIds.length > 0 ? selectedProductIds : undefined,
      providersInclude: selectedProvidersInclude,
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted,
    }),
    [startDateForApi, endDateForApi, selectedProductIds, selectedProvidersInclude, isDiscounted]
  );

  const summaryQueryFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      province: provinceFilterForApi,
      retailers: selectedRetailerNames.length > 0 ? selectedRetailerNames : undefined,
      productIds: selectedProductIds.length > 0 ? selectedProductIds : undefined,
      providersInclude: selectedProvidersInclude,
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted,
    }),
    [
      startDateForApi,
      endDateForApi,
      provinceFilterForApi,
      selectedRetailerNames,
      selectedProductIds,
      selectedProvidersInclude,
      isDiscounted,
    ]
  );

  const productFilterCountFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      province: provinceFilterForApi,
      retailers: selectedRetailerNames.length > 0 ? selectedRetailerNames : undefined,
      providersInclude: selectedProvidersInclude,
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted,
    }),
    [
      startDateForApi,
      endDateForApi,
      provinceFilterForApi,
      selectedRetailerNames,
      selectedProvidersInclude,
      isDiscounted,
    ]
  );

  const retailerFilterCountFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      province: provinceFilterForApi,
      productIds: selectedProductIds.length > 0 ? selectedProductIds : undefined,
      providersInclude: selectedProvidersInclude,
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted,
    }),
    [
      startDateForApi,
      endDateForApi,
      provinceFilterForApi,
      selectedProductIds,
      selectedProvidersInclude,
      isDiscounted,
    ]
  );

  const filterOptionCountsSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi
      ? [
          "pricing-filter-option-counts",
          productFilterCountFilters,
          retailerFilterCountFilters,
        ]
      : null;

  const { data: filterOptionCounts, isLoading: isFilterOptionCountsLoading } = useSWR(
    filterOptionCountsSwrKey,
    async () => {
      const [productRows, retailerRows] = await Promise.all([
        fetchDistinctCounts("product_id", productFilterCountFilters),
        fetchDistinctCounts("retailer", retailerFilterCountFilters),
      ]);
      return { productRows, retailerRows };
    },
    { revalidateOnFocus: false }
  );

  const productPriceHistoryCountById = useMemo(
    () =>
      productPriceHistoryCountByIdFromAggregateRows(
        filterOptionCounts?.productRows,
        normalizeAggregateProductId
      ),
    [filterOptionCounts?.productRows]
  );

  const retailerPriceHistoryCountByName = useMemo(
    () => retailerPriceHistoryCountByNameFromAggregateRows(filterOptionCounts?.retailerRows),
    [filterOptionCounts?.retailerRows]
  );

  const pricingSummaryIsVisible = useMemo(
    () =>
      Boolean(
        userMayViewPricingReports &&
          beefAiConfigured() &&
          startDateForApi &&
          endDateForApi &&
          dateFrom.isValid() &&
          dateTo.isValid() &&
          !dateFrom.startOf("day").isAfter(dateTo.endOf("day"))
      ),
    [userMayViewPricingReports, startDateForApi, endDateForApi, dateFrom, dateTo]
  );

  const productSnapshotIsVisible = useMemo(
    () =>
      Boolean(
        userMayViewPricingReports &&
          beefAiConfigured() &&
          hasSelectedProducts &&
          startDateForApi &&
          endDateForApi &&
          dateFrom.isValid() &&
          dateTo.isValid() &&
          !dateFrom.startOf("day").isAfter(dateTo.endOf("day"))
      ),
    [
      userMayViewPricingReports,
      hasSelectedProducts,
      startDateForApi,
      endDateForApi,
      dateFrom,
      dateTo,
    ]
  );

  useEffect(() => {
    setAggregatePage(0);
    setAggregateSort(DEFAULT_PRICE_HISTORY_AGGREGATE_SORT);
  }, [priceHistoryQueryFilters]);

  const aggregateTableSortUi = useMemo(
    () => aggregateTableSortUiStateFromSortParam(aggregateSort),
    [aggregateSort]
  );

  const handleAggregateTableSortChange = useCallback((signedHeaderId) => {
    const nextSort = aggregateTableSortParamFromSignedHeaderId(signedHeaderId);
    if (!nextSort) return;
    setAggregateSort(nextSort);
    setAggregatePage(0);
  }, []);

  const priceAggregateGroupCountSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi
      ? ["pricing-aggregate-group-count", priceHistoryQueryFilters]
      : null;

  const {
    data: aggregateGroupCount,
    isLoading: isAggregateGroupCountLoading,
  } = useSWR(priceAggregateGroupCountSwrKey, () => fetchPriceHistoryAggregateGroupCount(priceHistoryQueryFilters), {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (aggregateGroupCount == null) return;
    const maxPage = Math.max(0, Math.ceil(aggregateGroupCount / aggregateRowsPerPage) - 1);
    if (aggregatePage > maxPage) {
      setAggregatePage(maxPage);
    }
  }, [aggregateGroupCount, aggregateRowsPerPage, aggregatePage]);

  useEffect(() => {
    setSelectedAggregateDrilldown(null);
    setBreakdownPresentation("table");
  }, [priceHistoryQueryFilters]);

  useEffect(() => {
    if (
      selectedProvince &&
      provinceChoices.length > 0 &&
      !provinceChoices.some((opt) => opt.value === selectedProvince.value)
    ) {
      setSelectedProvince(null);
    }
  }, [selectedProvince, provinceChoices]);

  const priceAggregateSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi
      ? [
          "pricing-aggregate-weight",
          priceHistoryQueryFilters,
          aggregatePage,
          aggregateRowsPerPage,
          aggregateSort,
        ]
      : null;

  const {
    data: priceAggregatePayload,
    error: priceAggregateError,
    isLoading: isPriceAggregateLoading,
  } = useSWR(
    priceAggregateSwrKey,
    () =>
      fetchPriceHistoryAggregates(priceHistoryQueryFilters, {
        page: aggregatePage + 1,
        limit: aggregateRowsPerPage,
        sort: aggregateSort,
      }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const serverAggregateRows = priceAggregatePayload?.data;
  const aggregateTotalGroupCount = aggregateGroupCount ?? 0;
  const aggregateInitialLoading =
    (isPriceAggregateLoading && priceAggregatePayload === undefined) ||
    (isAggregateGroupCountLoading && aggregateGroupCount === undefined);
  const aggregatePageRefreshing =
    (isPriceAggregateLoading && priceAggregatePayload !== undefined) ||
    (isAggregateGroupCountLoading && aggregateGroupCount !== undefined);

  const { data: publishedProducts, error: publishedProductsError } = useSWR(
    userMayViewPricingReports && beefAiConfigured() ? "pricing-products-catalog-with-ref-id" : null,
    () => fetchPublishedProducts({ withRefIdOnly: true }),
    { revalidateOnFocus: false, dedupingInterval: 60 * 60 * 1000 }
  );

  const publishedProductNameById = useMemo(() => {
    const nameByProductId = new Map();
    for (const catalogProduct of publishedProducts || []) {
      if (catalogProduct?.id) {
        nameByProductId.set(String(catalogProduct.id), catalogProduct.name || String(catalogProduct.id));
      }
    }
    return nameByProductId;
  }, [publishedProducts]);

  const aggregatePageProductIdsKey = useMemo(() => {
    const ids = new Set();
    for (const row of serverAggregateRows || []) {
      const id = normalizeAggregateProductId(row);
      if (id) ids.add(id);
    }
    return [...ids].sort().join(",");
  }, [serverAggregateRows]);

  const productNamesForAggregatePageKey =
    userMayViewPricingReports && beefAiConfigured() && aggregatePageProductIdsKey
      ? ["pricing-product-names-for-aggregate-page", aggregatePageProductIdsKey]
      : null;

  const { data: productsForAggregatePage } = useSWR(
    productNamesForAggregatePageKey,
    () => fetchProductsByIds(aggregatePageProductIdsKey.split(",").filter(Boolean)),
    { revalidateOnFocus: false }
  );

  const combinedProductNameById = useMemo(() => {
    const merged = new Map(publishedProductNameById);
    for (const row of productsForAggregatePage || []) {
      if (row?.id) {
        merged.set(String(row.id), row.name || merged.get(String(row.id)) || String(row.id));
      }
    }
    return merged;
  }, [publishedProductNameById, productsForAggregatePage]);

  const averageUnitPriceTableRows = useMemo(
    () => mapAggregateRowsToPriceReportRows(serverAggregateRows ?? [], combinedProductNameById),
    [serverAggregateRows, combinedProductNameById]
  );

  const aggregateRowKeysForCovers = useMemo(
    () =>
      averageUnitPriceTableRows
        .map((row) => buildAggregateGroupRowKey(row.drilldown))
        .join(","),
    [averageUnitPriceTableRows]
  );

  const aggregateCoverSwrKey =
    pricingReportResultsView === "card" &&
    userMayViewPricingReports &&
    beefAiConfigured() &&
    startDateForApi &&
    endDateForApi &&
    averageUnitPriceTableRows.length > 0
      ? [
          "pricing-aggregate-covers",
          priceHistoryQueryFilters,
          aggregatePage,
          aggregateRowsPerPage,
          aggregateRowKeysForCovers,
        ]
      : null;

  const {
    data: aggregateCoverUrlByRowKey,
    error: aggregateCoverFetchError,
    isLoading: isAggregateCoversLoading,
  } = useSWR(
    aggregateCoverSwrKey,
    () =>
      fetchAggregateGroupCoverImagesForPage(
        priceHistoryQueryFilters,
        averageUnitPriceTableRows
      ),
    { revalidateOnFocus: false, keepPreviousData: false }
  );

  const coverUrlLookupForCardGrid = useMemo(() => {
    if (aggregateCoverFetchError) return new Map();
    return aggregateCoverUrlByRowKey instanceof Map ? aggregateCoverUrlByRowKey : new Map();
  }, [aggregateCoverUrlByRowKey, aggregateCoverFetchError]);

  const breakdownSwrKey =
    userMayViewPricingReports && beefAiConfigured() && selectedAggregateDrilldown
      ? ["pricing-breakdown", priceHistoryQueryFilters, selectedAggregateDrilldown.drilldown]
      : null;

  const {
    data: breakdownRows,
    error: breakdownError,
    isLoading: isBreakdownLoading,
  } = useSWR(
    breakdownSwrKey,
    () => fetchPriceHistoryBreakdown(priceHistoryQueryFilters, selectedAggregateDrilldown.drilldown),
    { revalidateOnFocus: false }
  );

  const productSnapshotRetailerQueryFilters = useMemo(
    () => ({
      ...productSnapshotQueryFilters,
      province: provinceFilterForApi,
    }),
    [productSnapshotQueryFilters, provinceFilterForApi]
  );

  const productSnapshotProvinceQueryFilters = useMemo(
    () => ({
      ...productSnapshotQueryFilters,
      retailers: selectedRetailerNames.length > 0 ? selectedRetailerNames : undefined,
    }),
    [productSnapshotQueryFilters, selectedRetailerNames]
  );

  const showProductSnapshotRetailerChart = Boolean(
    productSnapshotIsVisible && selectedRetailers.length === 0
  );

  const productSnapshotRetailerSwrKey = showProductSnapshotRetailerChart
    ? ["product-snapshot-retailer", productSnapshotRetailerQueryFilters]
    : null;

  const {
    data: productSnapshotRetailerRows,
    error: retailerSnapshotError,
    isLoading: isRetailerSnapshotLoading,
  } = useSWR(
    productSnapshotRetailerSwrKey,
    () => fetchProductAnalyticsRetailerSnapshot(productSnapshotRetailerQueryFilters),
    { revalidateOnFocus: false }
  );

  const productSnapshotProvinceSwrKey = productSnapshotIsVisible
    ? ["product-snapshot-province", productSnapshotProvinceQueryFilters]
    : null;

  const {
    data: productSnapshotProvinceRows,
    error: provinceSnapshotError,
    isLoading: isProvinceSnapshotLoading,
  } = useSWR(
    productSnapshotProvinceSwrKey,
    () => fetchProductAnalyticsAverageUnitPriceByProvince(productSnapshotProvinceQueryFilters),
    { revalidateOnFocus: false }
  );

  const productSnapshotDisplayName = useMemo(
    () => formatSelectedProductsChartLabel(selectedProducts, formatProductOptionLabel),
    [selectedProducts]
  );

  const quarterlySummarySwrKey = pricingSummaryIsVisible
    ? ["pricing-quarterly-summary", summaryQueryFilters]
    : null;

  const {
    data: quarterlySummaryStats,
    error: quarterlySummaryError,
    isLoading: isQuarterlySummaryLoading,
  } = useSWR(
    quarterlySummarySwrKey,
    () => fetchQuarterlyProductSummaryStats(summaryQueryFilters),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const productChartUsesWeeklyBuckets = useMemo(
    () => shouldUseWeeklyTimeBuckets(startDateForApi, endDateForApi),
    [startDateForApi, endDateForApi]
  );

  const productChartGranularity = productChartUsesWeeklyBuckets ? "week" : "month";

  const monthlyByProductSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi
      ? ["pricing-chart-monthly-product", priceHistoryQueryFilters, productChartGranularity]
      : null;

  const {
    data: monthlyByProductRows = [],
    error: monthlyByProductError,
    isLoading: isMonthlyByProductLoading,
  } = useSWR(
    monthlyByProductSwrKey,
    () =>
      fetchTimeSeriesAverageByProduct(priceHistoryQueryFilters, {
        granularity: productChartGranularity,
      }),
    { revalidateOnFocus: false }
  );

  const monthlyChartProductIdsKey = useMemo(() => {
    const ids = new Set();
    for (const row of monthlyByProductRows || []) {
      const id = normalizeAggregateProductId(row);
      if (id) ids.add(id);
    }
    return [...ids].sort().join(",");
  }, [monthlyByProductRows]);

  const {
    data: productsForMonthlyCharts,
    isLoading: isProductsForMonthlyChartsLoading,
  } = useSWR(
    monthlyChartProductIdsKey
      ? ["pricing-chart-product-names", monthlyChartProductIdsKey]
      : null,
    () => fetchProductsByIds(monthlyChartProductIdsKey.split(",").filter(Boolean)),
    { revalidateOnFocus: false }
  );

  const isMonthlyProductChartLoading =
    isMonthlyByProductLoading ||
    (Boolean(monthlyChartProductIdsKey) && isProductsForMonthlyChartsLoading);

  const breakdownInitialLoading = isBreakdownLoading && breakdownRows === undefined;

  const chartProductNameById = useMemo(() => {
    const merged = new Map(combinedProductNameById);
    for (const product of productsForMonthlyCharts || []) {
      if (product?.id) {
        merged.set(String(product.id), product.name || merged.get(String(product.id)) || String(product.id));
      }
    }
    return merged;
  }, [combinedProductNameById, productsForMonthlyCharts]);

  const monthlyByRetailerSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi && hasSelectedProducts
      ? ["pricing-chart-monthly-retailer", priceHistoryQueryFilters]
      : null;

  const {
    data: monthlyByRetailerRows = [],
    error: monthlyByRetailerError,
    isLoading: isMonthlyByRetailerLoading,
  } = useSWR(
    monthlyByRetailerSwrKey,
    () => fetchMonthlyAverageByRetailer(priceHistoryQueryFilters),
    { revalidateOnFocus: false }
  );

  const monthlyByProvinceSwrKey =
    userMayViewPricingReports && beefAiConfigured() && startDateForApi && endDateForApi && hasSelectedProducts
      ? ["pricing-chart-monthly-province", priceHistoryQueryFilters]
      : null;

  const {
    data: monthlyByProvinceRows = [],
    error: monthlyByProvinceError,
    isLoading: isMonthlyByProvinceLoading,
  } = useSWR(
    monthlyByProvinceSwrKey,
    () => fetchMonthlyAverageByProvince(priceHistoryQueryFilters),
    { revalidateOnFocus: false }
  );

  const quarterlyAverageSwrKey =
    userMayViewPricingReports && beefAiConfigured() && hasSelectedProducts
      ? ["pricing-chart-quarterly-product", priceHistoryQueryFilters]
      : null;

  const {
    data: quarterlyRows = [],
    error: quarterlyError,
    isLoading: isQuarterlyLoading,
  } = useSWR(
    quarterlyAverageSwrKey,
    () =>
      fetchQuarterlyAverageByProduct(priceHistoryQueryFilters, {
        lookbackYears: QUARTERLY_LOOKBACK_MAX_YEARS,
      }),
    { revalidateOnFocus: false }
  );

  const availableQuarterlyLookbackYears = useMemo(
    () => computeAvailableQuarterlyLookbackYears(quarterlyRows),
    [quarterlyRows]
  );

  useEffect(() => {
    if (!availableQuarterlyLookbackYears.length) return;
    if (!availableQuarterlyLookbackYears.includes(quarterlyLookbackYears)) {
      setQuarterlyLookbackYears(
        availableQuarterlyLookbackYears[availableQuarterlyLookbackYears.length - 1]
      );
    }
  }, [availableQuarterlyLookbackYears, quarterlyLookbackYears]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      setProductChartCombinedView(false);
    }
  }, [selectedProducts.length]);

  const monthlyByProductFacetChart = useMemo(
    () =>
      buildTimeBucketFacetLineSeries({
        rows: monthlyByProductRows,
        dimension: "product",
        granularity: productChartGranularity,
        maxSeries: selectedProducts.length > 0 ? selectedProducts.length : 10,
        productNameById: chartProductNameById,
      }),
    [monthlyByProductRows, chartProductNameById, productChartGranularity, selectedProducts.length]
  );

  const monthlyByProductCandlestickChart = useMemo(
    () =>
      hasSelectedProducts
        ? buildTimeBucketCandlestickSeries({
            rows: monthlyByProductRows,
            dimension: "product",
            granularity: productChartGranularity,
            maxSeries: 1,
            productNameById: chartProductNameById,
            poolAsOne: selectedProducts.length > 1,
            pooledLabel:
              formatSelectedProductsChartLabel(selectedProducts, formatProductOptionLabel) ||
              "Selected products",
          })
        : null,
    [
      monthlyByProductRows,
      hasSelectedProducts,
      selectedProducts,
      chartProductNameById,
      productChartGranularity,
    ]
  );

  const monthlyByProductCombinedChart = useMemo(
    () =>
      buildTimeBucketSmoothedCombinedLineSeries(
        {
          rows: monthlyByProductRows,
          dimension: "product",
          granularity: productChartGranularity,
          maxSeries: soleSelectedProduct?.id ? null : selectedProducts.length > 0 ? selectedProducts.length : 10,
          productNameById: chartProductNameById,
        },
        { dimension: "product" }
      ),
    [monthlyByProductRows, soleSelectedProduct, selectedProducts.length, chartProductNameById, productChartGranularity]
  );

  const monthlyByRetailerChart = useMemo(
    () =>
      buildTimeBucketFacetLineSeries({
        rows: monthlyByRetailerRows,
        dimension: "retailer",
      }),
    [monthlyByRetailerRows]
  );

  const monthlyByRetailerCombinedChart = useMemo(
    () =>
      buildTimeBucketSmoothedCombinedLineSeries({
        rows: monthlyByRetailerRows,
      }),
    [monthlyByRetailerRows]
  );

  const monthlyByProvinceChart = useMemo(
    () =>
      buildTimeBucketFacetLineSeries({
        rows: monthlyByProvinceRows,
        dimension: "province",
        maxSeries: selectedProvince ? null : 10,
      }),
    [monthlyByProvinceRows, selectedProvince]
  );

  const monthlyByProvinceCombinedChart = useMemo(
    () =>
      buildTimeBucketSmoothedCombinedLineSeries(
        {
          rows: monthlyByProvinceRows,
          maxSeries: selectedProvince ? null : 10,
        },
        { dimension: "province" }
      ),
    [monthlyByProvinceRows, selectedProvince]
  );

  const quarterlyChart = useMemo(
    () => buildQuarterlySingleSeriesForLookback(quarterlyRows, quarterlyLookbackYears),
    [quarterlyRows, quarterlyLookbackYears]
  );

  const retailerChoices = useMemo(() => {
    const retailerNames = (distinctProvinceRetailerCounts?.retailers || [])
      .map((aggregateRow) => aggregateRow.retailer)
      .filter((name) => name != null && String(name).trim());
    return [...new Set(retailerNames)]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((name) => ({
        name: String(name),
        priceHistoryCount: retailerPriceHistoryCountByName.get(String(name)) ?? 0,
      }));
  }, [distinctProvinceRetailerCounts, retailerPriceHistoryCountByName]);

  const providerChoices = useMemo(() => {
    const providerNames = (distinctProvinceRetailerCounts?.providers || [])
      .map((aggregateRow) => aggregateRow.provider)
      .filter((name) => name != null && String(name).trim());
    return [...new Set(providerNames)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [distinctProvinceRetailerCounts]);

  useEffect(() => {
    if (!providerChoices.length) return;
    setSelectedProvidersInclude((current) => {
      const validSelection = current.filter((provider) => providerChoices.includes(provider));
      if (validSelection.length > 0) return validSelection;
      return DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE.filter((provider) =>
        providerChoices.includes(provider)
      );
    });
  }, [providerChoices]);

  const productFilterChoices = useMemo(() => {
    const catalog = publishedProducts || [];
    const uniqueProductsById = new Map();
    for (const catalogProduct of catalog) {
      if (catalogProduct?.id && String(catalogProduct.ref_id || "").trim()) {
        uniqueProductsById.set(catalogProduct.id, {
          ...catalogProduct,
          priceHistoryCount: productPriceHistoryCountById.get(String(catalogProduct.id)) ?? 0,
        });
      }
    }
    return [...uniqueProductsById.values()].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    );
  }, [publishedProducts, productPriceHistoryCountById]);

  useEffect(() => {
    if (!productFilterChoices.length) return;
    setSelectedProducts((current) =>
      current.filter((product) =>
        productFilterChoices.some((choice) => choice.id === product.id)
      )
    );
  }, [productFilterChoices]);

  useEffect(() => {
    if (!retailerChoices.length) return;
    setSelectedRetailers((current) =>
      current.filter((retailer) =>
        retailerChoices.some((choice) => choice.name === retailer.name)
      )
    );
  }, [retailerChoices]);

  const handleSelectProductById = useCallback(
    (productId) => {
      if (!productId) return;
      const normalizedId = String(productId);
      const selectedFromFilters = productFilterChoices.find(
        (product) => String(product?.id) === normalizedId
      );
      if (selectedFromFilters) {
        setSelectedProducts([selectedFromFilters]);
        return;
      }
      setSelectedProducts([
        {
          id: normalizedId,
          name: chartProductNameById.get(normalizedId) || normalizedId,
        },
      ]);
    },
    [productFilterChoices, chartProductNameById]
  );

  const handleSelectProvinceFromDrilldown = useCallback(
    (drilldownProvince) => {
      if (!drilldownProvince) return;
      const provinceCode = toProv(drilldownProvince);
      const matchedProvinceOption = provinceChoices.find(
        (provinceOption) => provinceOption.value === provinceCode
      );
      if (matchedProvinceOption) {
        setSelectedProvince(matchedProvinceOption);
        return;
      }
      const normalizedDrilldownProvince = String(drilldownProvince).trim().toLowerCase();
      const matchedByLabel = provinceChoices.find(
        (provinceOption) => provinceOption.label.trim().toLowerCase() === normalizedDrilldownProvince
      );
      if (matchedByLabel) {
        setSelectedProvince(matchedByLabel);
      }
    },
    [provinceChoices]
  );

  const handleSelectRetailerFromDrilldown = useCallback(
    (retailerName) => {
      const normalizedRetailerName = String(retailerName || "").trim();
      if (!normalizedRetailerName) return;
      const matchedRetailer = retailerChoices.find(
        (retailer) => retailer.name === normalizedRetailerName
      );
      setSelectedRetailers([
        matchedRetailer ?? {
          name: normalizedRetailerName,
          priceHistoryCount:
            retailerPriceHistoryCountByName.get(normalizedRetailerName) ?? 0,
        },
      ]);
    },
    [retailerChoices, retailerPriceHistoryCountByName]
  );

  const handleMonthlyProductLineClick = useCallback(
    (productId) => handleSelectProductById(productId),
    [handleSelectProductById]
  );

  const handleCloseAggregateBreakdown = useCallback(() => {
    setSelectedAggregateDrilldown(null);
    setBreakdownPresentation("table");
    setGalleryFocusedRecordId(null);
    setGalleryInitialPanelMode("view");
  }, []);

  const handleAggregateCountClick = useCallback((row) => {
    setSelectedAggregateDrilldown(row);
    setBreakdownPresentation("table");
    setGalleryFocusedRecordId(null);
    setGalleryInitialPanelMode("view");
  }, []);

  const handleAggregateCardClick = useCallback((row) => {
    setSelectedAggregateDrilldown(row);
    setBreakdownPresentation("gallery");
    setGalleryFocusedRecordId(null);
    setGalleryInitialPanelMode("view");
  }, []);

  const handleBreakdownTableViewRecord = useCallback((recordId) => {
    if (!recordId) return;
    setGalleryFocusedRecordId(recordId);
    setGalleryInitialPanelMode("view");
    setBreakdownPresentation("gallery");
  }, []);

  const handlePricingReportResultsViewChange = useCallback((nextView) => {
    setPricingReportResultsView(nextView);
    try {
      sessionStorage.setItem(PRICING_REPORT_RESULTS_VIEW_SESSION_KEY, nextView);
    } catch (_) {
      /* sessionStorage unavailable */
    }
    setAggregatePage(0);
    setAggregateRowsPerPage(
      nextView === "card" ? DEFAULT_AGGREGATE_GRID_PAGE_SIZE : DEFAULT_AGGREGATE_PAGE_SIZE
    );
    setSelectedAggregateDrilldown(null);
    setBreakdownPresentation("table");
  }, []);

  const breakdownTableDialogOpen =
    Boolean(selectedAggregateDrilldown) && breakdownPresentation === "table";
  const breakdownGalleryDialogOpen =
    Boolean(selectedAggregateDrilldown) && breakdownPresentation === "gallery";

  const breakdownUnitChoices = useMemo(() => {
    const units = new Set(["kg"]);
    for (const row of breakdownRows || []) {
      const unit = typeof row?.unit === "string" ? row.unit.trim() : "";
      if (unit) units.add(unit);
    }
    return [...units].sort((a, b) => String(a).localeCompare(String(b)));
  }, [breakdownRows]);

  const resolveProvinceForApi = useCallback(
    (provinceCode) => {
      if (!provinceCode) return "";
      return provinceCodeToBeefAiProvinceString.get(provinceCode) ?? provinceCode;
    },
    [provinceCodeToBeefAiProvinceString]
  );

  const handlePriceHistoryRecordSave = useCallback(
    async (recordId, formValues) => {
      await updatePriceHistoryRecord(recordId, formValues);

      const revalidationTargets = [
        breakdownSwrKey,
        priceAggregateSwrKey,
        aggregateCoverSwrKey,
        monthlyByProductSwrKey,
        monthlyByRetailerSwrKey,
        monthlyByProvinceSwrKey,
        quarterlyAverageSwrKey,
        productSnapshotRetailerSwrKey,
        productSnapshotProvinceSwrKey,
        quarterlySummarySwrKey,
        filterOptionCountsSwrKey,
      ].filter(Boolean);

      await Promise.all(revalidationTargets.map((swrKey) => mutate(swrKey)));
    },
    [
      breakdownSwrKey,
      priceAggregateSwrKey,
      aggregateCoverSwrKey,
      monthlyByProductSwrKey,
      monthlyByRetailerSwrKey,
      monthlyByProvinceSwrKey,
      quarterlyAverageSwrKey,
      productSnapshotRetailerSwrKey,
      productSnapshotProvinceSwrKey,
      quarterlySummarySwrKey,
      filterOptionCountsSwrKey,
    ]
  );

  const handlePriceHistoryRecordVoid = useCallback(
    async (recordId) => {
      await voidPriceHistoryRecord(recordId);

      const revalidationTargets = [
        breakdownSwrKey,
        priceAggregateSwrKey,
        aggregateCoverSwrKey,
        monthlyByProductSwrKey,
        monthlyByRetailerSwrKey,
        monthlyByProvinceSwrKey,
        quarterlyAverageSwrKey,
        productSnapshotRetailerSwrKey,
        productSnapshotProvinceSwrKey,
        quarterlySummarySwrKey,
        filterOptionCountsSwrKey,
      ].filter(Boolean);

      await Promise.all(revalidationTargets.map((swrKey) => mutate(swrKey)));
    },
    [
      breakdownSwrKey,
      priceAggregateSwrKey,
      aggregateCoverSwrKey,
      monthlyByProductSwrKey,
      monthlyByRetailerSwrKey,
      monthlyByProvinceSwrKey,
      quarterlyAverageSwrKey,
      productSnapshotRetailerSwrKey,
      productSnapshotProvinceSwrKey,
      quarterlySummarySwrKey,
      filterOptionCountsSwrKey,
    ]
  );

  const aggregateServerPagination = useMemo(
    () => ({
      page: aggregatePage,
      rowsPerPage: aggregateRowsPerPage,
      totalCount: aggregateTotalGroupCount,
      onPageChange: (_, nextPage) => setAggregatePage(nextPage),
      onRowsPerPageChange: (event) => {
        setAggregateRowsPerPage(parseInt(event.target.value, 10));
        setAggregatePage(0);
      },
    }),
    [aggregatePage, aggregateRowsPerPage, aggregateTotalGroupCount]
  );

  const anyPricingFetchError = priceAggregateError || filterOptionsError || publishedProductsError;
  const isPricingDataBusy = isPriceAggregateLoading;

  const pricingExportFileName = useMemo(() => {
    const fromPart = startDateForApi || "from";
    const toPart = (endDateForApi || "to").slice(0, 10);
    return `pricing-report-${fromPart}_to_${toPart}.csv`;
  }, [startDateForApi, endDateForApi]);

  const pricingRawExportFileName = useMemo(() => {
    const fromPart = startDateForApi || "from";
    const toPart = (endDateForApi || "to").slice(0, 10);
    return `pricing-report-records-${fromPart}_to_${toPart}.csv`;
  }, [startDateForApi, endDateForApi]);

  const hasSelectedProduct = hasSelectedProducts;

  const dateRangeValid = useMemo(
    () =>
      dateFrom.isValid() &&
      dateTo.isValid() &&
      !dateFrom.startOf("day").isAfter(dateTo.endOf("day")),
    [dateFrom, dateTo]
  );

  const includedPricingChartKeys = useMemo(
    () =>
      pricingReportIncludedChartKeys({
        hasSelectedProduct,
        monthlyByProductFacetChart,
        monthlyByProductCandlestickChart,
        monthlyByProductCombinedChart,
        productChartCombinedView,
        monthlyByRetailerChart,
        monthlyByRetailerCombinedChart,
        retailerChartCombinedView,
        monthlyByProvinceChart,
        monthlyByProvinceCombinedChart,
        provinceChartCombinedView,
        quarterlyChart,
      }),
    [
      hasSelectedProduct,
      monthlyByProductFacetChart,
      monthlyByProductCandlestickChart,
      monthlyByProductCombinedChart,
      productChartCombinedView,
      monthlyByRetailerChart,
      monthlyByRetailerCombinedChart,
      retailerChartCombinedView,
      monthlyByProvinceChart,
      monthlyByProvinceCombinedChart,
      provinceChartCombinedView,
      quarterlyChart,
    ]
  );

  const includedSnapshotChartKeys = useMemo(
    () =>
      pricingReportIncludedSnapshotChartKeys({
        productSnapshotIsVisible,
        selectedRetailers,
        retailerSnapshotRows: productSnapshotRetailerRows,
        provinceSnapshotRows: productSnapshotProvinceRows,
        retailerSnapshotError,
        provinceSnapshotError,
      }),
    [
      productSnapshotIsVisible,
      selectedRetailers,
      productSnapshotRetailerRows,
      productSnapshotProvinceRows,
      retailerSnapshotError,
      provinceSnapshotError,
    ]
  );

  const allIncludedChartKeys = useMemo(
    () => pricingReportAllIncludedChartKeys(includedSnapshotChartKeys, includedPricingChartKeys),
    [includedSnapshotChartKeys, includedPricingChartKeys]
  );

  const includedPricingChartsReady = useMemo(
    () =>
      pricingReportIncludedChartsReady({
        includedChartKeys: includedPricingChartKeys,
        hasSelectedProduct,
        isMonthlyProductChartLoading,
        isMonthlyByRetailerLoading,
        isMonthlyByProvinceLoading,
        isQuarterlyLoading,
      }),
    [
      includedPricingChartKeys,
      hasSelectedProduct,
      isMonthlyProductChartLoading,
      isMonthlyByRetailerLoading,
      isMonthlyByProvinceLoading,
      isQuarterlyLoading,
    ]
  );

  const showQuarterlyKpisForPrint = useMemo(
    () =>
      pricingSummaryIsVisible &&
      !quarterlySummaryError &&
      quarterlySummaryStats != null,
    [pricingSummaryIsVisible, quarterlySummaryError, quarterlySummaryStats]
  );

  const isCardCoversLoading =
    pricingReportResultsView === "card" &&
    Boolean(aggregateCoverSwrKey) &&
    isAggregateCoversLoading;

  const pricingReportPrintIsDisabledValue = useMemo(
    () =>
      pricingReportPrintIsDisabled({
        aggregateTotalGroupCount,
        startDateForApi,
        endDateForApi,
        dateFromValid: dateFrom.isValid(),
        dateToValid: dateTo.isValid(),
        dateRangeValid,
        isPricingDataBusy,
        aggregateInitialLoading,
        isPricingPrintPreparing,
        pricingSummaryIsVisible,
        productSnapshotIsVisible,
        showProductSnapshotRetailerChart,
        isQuarterlySummaryLoading,
        quarterlySummaryStats,
        quarterlySummaryError,
        isRetailerSnapshotLoading,
        isProvinceSnapshotLoading,
        includedChartsReady: includedPricingChartsReady,
        isCardCoversLoading,
      }),
    [
      aggregateTotalGroupCount,
      startDateForApi,
      endDateForApi,
      dateFrom,
      dateTo,
      dateRangeValid,
      isPricingDataBusy,
      aggregateInitialLoading,
      isPricingPrintPreparing,
      pricingSummaryIsVisible,
      productSnapshotIsVisible,
      showProductSnapshotRetailerChart,
      isQuarterlySummaryLoading,
      quarterlySummaryStats,
      quarterlySummaryError,
      isRetailerSnapshotLoading,
      isProvinceSnapshotLoading,
      includedPricingChartsReady,
      isCardCoversLoading,
    ]
  );

  const setPricingChartRefForKey = useCallback((chartKey) => {
    return (chartComponentRef) => {
      const chartInstance = chartComponentRef?.isFacetGridComposite
        ? chartComponentRef
        : chartComponentRef?.chart ?? chartComponentRef;
      if (chartInstance) {
        pricingReportChartRefsByKey.current[chartKey] = chartInstance;
      } else {
        delete pricingReportChartRefsByKey.current[chartKey];
      }
    };
  }, []);

  const handlePrintPricingReport = useCallback(async () => {
    const previousDocumentTitle = document.title;
    const pricingChartsAccordionExpandedBeforeCapture = pricingChartsAccordionExpanded;
    const productSnapshotAccordionExpandedBeforeCapture = productSnapshotAccordionExpanded;

    setIsPricingPrintPreparing(true);
    setPricingPrintError("");

    try {
      setPricingChartsAccordionExpanded(true);
      if (productSnapshotIsVisible) {
        setProductSnapshotAccordionExpanded(true);
      }

      await accordionCaptureWaiters.waitForPricingReportAccordionsExpandedForCapture({
        pricingChartsWasCollapsed: !pricingChartsAccordionExpandedBeforeCapture,
        productSnapshotWasCollapsed:
          productSnapshotIsVisible && !productSnapshotAccordionExpandedBeforeCapture,
      });

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const restoreChartPlotHeightsAfterPrintCapture = resizePricingReportChartsForPrint(
        pricingReportChartRefsByKey.current,
        allIncludedChartKeys
      );

      let chartImagesByKey = {};
      try {
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        await waitForPricingReportChartCanvasesReady(
          pricingReportChartRefsByKey.current,
          allIncludedChartKeys
        );
        await new Promise((resolve) => setTimeout(resolve, CAPTURE_DELAY_BEFORE_PNG_MS));

        chartImagesByKey = capturePricingReportChartImagesForPrint(
          pricingReportChartRefsByKey.current,
          allIncludedChartKeys
        );
      } finally {
        restoreChartPlotHeightsAfterPrintCapture();
      }

      setPricingChartsAccordionExpanded(pricingChartsAccordionExpandedBeforeCapture);
      if (productSnapshotIsVisible) {
        setProductSnapshotAccordionExpanded(productSnapshotAccordionExpandedBeforeCapture);
      }

      const printSnapshot = buildPricingReportPrintSnapshot({
        startDateForApi,
        endDateForApi,
        dateFromDisplay: startDateForApi,
        dateToDisplay: endDateForApi ? endDateForApi.slice(0, 10) : "",
        productLabel: formatSelectedProductsPrintLabel(selectedProducts, formatProductOptionLabel),
        selectedProvince,
        retailerLabel: formatSelectedRetailersPrintLabel(selectedRetailers),
        summaryCardSelectedRetailers: selectedRetailers,
        selectedProvidersInclude,
        isDiscounted,
        quarterlyLookbackYears,
        hasSelectedProduct,
        selectedProductCount: selectedProducts.length,
        productSnapshotDisplayName,
        showQuarterlyKpis: showQuarterlyKpisForPrint,
        quarterlySummaryStats,
        chartImagesByKey,
        averageUnitPriceTableRows,
        aggregatePage,
        aggregateRowsPerPage,
        aggregateTotalGroupCount,
        tableTitle: AGGREGATE_RESULTS_PRINT_TITLE,
        tableColumns: PRINT_AGGREGATE_TABLE_COLUMNS,
        resultsView: pricingReportResultsView,
        coverUrlByRowKey: coverUrlLookupForCardGrid,
        placeholderImageUrl: retailer?.default_image
          ? `${process.env.REACT_APP_API_URL}/assets/${retailer.default_image}`
          : null,
        retailerLogoAssetId: retailer?.logo ?? null,
        retailerLogoAlt:
          retailer?.retailer_name || retailer?.title || retailer?.meta || "Retailer logo",
        monthlyByProductChartGranularity: productChartGranularity,
        productChartCombinedView,
        retailerChartCombinedView,
        provinceChartCombinedView,
      });

      flushSync(() => {
        setPricingReportPrintSnapshot(printSnapshot);
      });

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await waitForElementImages(document.getElementById("pricing-report-print-root"));

      const endDateSlice = (endDateForApi || "").slice(0, 10);
      document.title = `pricing-report-${startDateForApi}_to_${endDateSlice}-page-${aggregatePage + 1}`;

      await openPricingReportPrintDialog();
    } catch (err) {
      setPricingPrintError(
        err?.message || "Something went wrong while opening the print dialog."
      );
    } finally {
      setPricingChartsAccordionExpanded(pricingChartsAccordionExpandedBeforeCapture);
      if (productSnapshotIsVisible) {
        setProductSnapshotAccordionExpanded(productSnapshotAccordionExpandedBeforeCapture);
      }
      setPricingReportPrintSnapshot(null);
      setIsPricingPrintPreparing(false);
      document.title = previousDocumentTitle;
    }
  }, [
    pricingChartsAccordionExpanded,
    productSnapshotAccordionExpanded,
    productSnapshotIsVisible,
    accordionCaptureWaiters,
    allIncludedChartKeys,
    startDateForApi,
    endDateForApi,
    selectedProducts,
    selectedProvince,
    selectedRetailers,
    selectedProvidersInclude,
    isDiscounted,
    quarterlyLookbackYears,
    hasSelectedProduct,
    productSnapshotDisplayName,
    showQuarterlyKpisForPrint,
    quarterlySummaryStats,
    averageUnitPriceTableRows,
    aggregatePage,
    aggregateRowsPerPage,
    aggregateTotalGroupCount,
    pricingReportResultsView,
    coverUrlLookupForCardGrid,
    retailer,
    productChartGranularity,
    productChartCombinedView,
    retailerChartCombinedView,
    provinceChartCombinedView,
  ]);

  const loadPricingExportRows = useCallback(async () => {
    setAggregateDownloadError("");
    const allAggregateRows = await fetchAllPriceHistoryAggregates(priceHistoryQueryFilters);
    const allAggregateProductIds = [
      ...new Set(
        (allAggregateRows || [])
          .map((row) => normalizeAggregateProductId(row))
          .filter(Boolean)
      ),
    ];
    const exportProducts = await fetchProductsByIds(allAggregateProductIds);
    const mergedNameById = new Map(publishedProductNameById);
    for (const product of exportProducts || []) {
      if (product?.id) {
        mergedNameById.set(String(product.id), product.name || mergedNameById.get(String(product.id)));
      }
    }
    const mappedRows = mapAggregateRowsToPriceReportRows(allAggregateRows, mergedNameById);
    return mappedRows.map((row) => ({
      ...row,
      avgUnit: formatAggregateCurrency(row.avgUnit),
      lowUnit: formatAggregateCurrency(row.lowUnit),
      highUnit: formatAggregateCurrency(row.highUnit),
      avgTotalWeight: formatAggregateTotalWeight(row.avgTotalWeight),
      lowTotalWeight: formatAggregateTotalWeight(row.lowTotalWeight),
      highTotalWeight: formatAggregateTotalWeight(row.highTotalWeight),
      avgNetWeight: formatAggregateNetWeightKg(row.avgNetWeight),
      lowNetWeight: formatAggregateNetWeightKg(row.lowNetWeight),
      highNetWeight: formatAggregateNetWeightKg(row.highNetWeight),
    }));
  }, [priceHistoryQueryFilters, publishedProductNameById]);

  const loadPricingRawExportRows = useCallback(async () => {
    setAggregateDownloadError("");
    const allRecords = await fetchAllPriceHistoryRecords(priceHistoryQueryFilters);
    const productIds = [
      ...new Set(
        (allRecords || [])
          .map((row) => normalizeAggregateProductId(row))
          .filter(Boolean)
      ),
    ];
    const exportProducts = await fetchProductsByIds(productIds);
    const mergedNameById = new Map(publishedProductNameById);
    for (const product of exportProducts || []) {
      if (product?.id) {
        mergedNameById.set(String(product.id), product.name || mergedNameById.get(String(product.id)));
      }
    }
    return mapPriceHistoryRecordsToRawExportRows(allRecords, mergedNameById);
  }, [priceHistoryQueryFilters, publishedProductNameById]);

  const pricingCsvExportOptions = useMemo(
    () => [
      {
        id: "grouped",
        label: "Summary report (grouped)",
        columns: PRICING_CSV_COLUMNS,
        fileName: pricingExportFileName,
        loadRows: loadPricingExportRows,
      },
      {
        id: "raw",
        label: "Detail report (all records)",
        columns: PRICING_RAW_CSV_COLUMNS,
        fileName: pricingRawExportFileName,
        loadRows: loadPricingRawExportRows,
      },
    ],
    [
      loadPricingExportRows,
      loadPricingRawExportRows,
      pricingExportFileName,
      pricingRawExportFileName,
    ]
  );

  const handlePricingCsvExportError = useCallback(
    (err) =>
      setAggregateDownloadError(
        err?.message || "Something went wrong while downloading pricing data."
      ),
    []
  );

  const pricingReportAggregateHeaderActions = useMemo(
    () => (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {pricingReportResultsView === "card" ? (
          <PricingReportCardSortSelect
            value={aggregateTableSignedHeaderIdFromUiState(aggregateTableSortUi)}
            onChange={handleAggregateTableSortChange}
          />
        ) : null}
        <PricingReportViewToggle
          view={pricingReportResultsView}
          onViewChange={handlePricingReportResultsViewChange}
        />
      </Stack>
    ),
    [
      aggregateTableSortUi,
      handleAggregateTableSortChange,
      handlePricingReportResultsViewChange,
      pricingReportResultsView,
    ]
  );

  const pricingReportFilterActions = useMemo(
    () => (
      <>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={pricingReportPrintIsDisabledValue}
          onClick={handlePrintPricingReport}
          startIcon={<Print />}
        >
          {isPricingPrintPreparing ? "Preparing report…" : "Print Report"}
        </Button>
        <PricingReportCsvExport
          disabled={isPricingDataBusy || aggregateTotalGroupCount === 0}
          options={pricingCsvExportOptions}
          onError={handlePricingCsvExportError}
        />
      </>
    ),
    [
      handlePrintPricingReport,
      pricingReportPrintIsDisabledValue,
      isPricingPrintPreparing,
      pricingCsvExportOptions,
      handlePricingCsvExportError,
      isPricingDataBusy,
      aggregateTotalGroupCount,
    ]
  );

  const shouldShowAggregateResults =
    !priceAggregateError && Boolean(startDateForApi && endDateForApi);
  const isLoadingFilterOptions =
    Boolean(filterOptionsSwrKey) && !distinctProvinceRetailerCounts && !filterOptionsError;

  const accessDeniedAlert = (
    <>
      <Alert severity="warning">
        You do not have access to pricing reports. If you believe this is a mistake, contact your Canada Beef
        administrator.
      </Alert>
      <Button component={Link} to="/dashboard" sx={{ mt: 2 }} variant="contained">
        Back to dashboard
      </Button>
    </>
  );

  if (!userMayViewPricingReports) {
    return <Box sx={{ py: 1 }}>{accessDeniedAlert}</Box>;
  }

  const pricingReportsMainContent = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        <Box className={insightsSectionClassName("insights-section--filter", "no-print")}>
          <PricingReportFilters
            dateFrom={dateFrom}
            dateTo={dateTo}
            setDateFrom={setDateFrom}
            setDateTo={setDateTo}
            provinceChoices={provinceChoices}
            selectedProvince={selectedProvince}
            setSelectedProvince={setSelectedProvince}
            productFilterChoices={productFilterChoices}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            retailerChoices={retailerChoices}
            selectedRetailers={selectedRetailers}
            setSelectedRetailers={setSelectedRetailers}
            providerChoices={providerChoices}
            selectedProvidersInclude={selectedProvidersInclude}
            setSelectedProvidersInclude={setSelectedProvidersInclude}
            isLoadingFilterOptions={isLoadingFilterOptions}
            isPublishedProductsLoading={
              (!publishedProducts && !publishedProductsError) || isFilterOptionCountsLoading
            }
            isRetailerFilterCountsLoading={isFilterOptionCountsLoading}
            formatProductOptionLabel={formatProductOptionLabel}
            formatProviderOptionLabel={formatProviderOptionLabel}
            filterActions={pricingReportFilterActions}
          />
        </Box>

        <Box className={insightsSectionClassName("insights-section--content")}>
          <Stack spacing={2}>
        {pricingSummaryIsVisible ? (
          <PricingReportQuarterlySummarySection
            quarterlySummaryStats={quarterlySummaryStats}
            isQuarterlySummaryLoading={isQuarterlySummaryLoading}
            quarterlySummaryError={quarterlySummaryError}
            selectedProvince={selectedProvince}
            selectedRetailers={selectedRetailers}
          />
        ) : null}

        {productSnapshotIsVisible ? (
          <>
            <ProductSnapshotPricingReportsBlock
              chartDateFrom={dateFrom}
              chartDateTo={dateTo}
              productName={productSnapshotDisplayName}
              selectedProvince={selectedProvince}
              selectedRetailers={selectedRetailers}
              retailerSnapshotRows={productSnapshotRetailerRows}
              isRetailerSnapshotLoading={isRetailerSnapshotLoading}
              retailerSnapshotError={retailerSnapshotError?.message}
              provinceSnapshotRows={productSnapshotProvinceRows}
              isProvinceSnapshotLoading={isProvinceSnapshotLoading}
              provinceSnapshotError={provinceSnapshotError?.message}
              accordionExpanded={productSnapshotAccordionExpanded}
              onAccordionExpandedChange={setProductSnapshotAccordionExpanded}
              onAccordionEntered={accordionCaptureWaiters.notifyProductSnapshotAccordionEntered}
              chartRefSnapshotRetailer={setPricingChartRefForKey("snapshot-retailer")}
              chartRefSnapshotProvince={setPricingChartRefForKey("snapshot-province")}
            />
          </>
        ) : null}

        <PricingReportCharts
          chartDateFrom={dateFrom}
          chartDateTo={dateTo}
          selectedProduct={soleSelectedProduct}
          selectedProductCount={selectedProducts.length}
          monthlyByProductFacetChart={monthlyByProductFacetChart}
          monthlyByProductCandlestickChart={monthlyByProductCandlestickChart}
          monthlyByProductCombinedChart={monthlyByProductCombinedChart}
          productChartCombinedView={productChartCombinedView}
          onProductChartCombinedViewChange={setProductChartCombinedView}
          monthlyByRetailerChart={monthlyByRetailerChart}
          monthlyByRetailerCombinedChart={monthlyByRetailerCombinedChart}
          retailerChartCombinedView={retailerChartCombinedView}
          onRetailerChartCombinedViewChange={setRetailerChartCombinedView}
          monthlyByProvinceChart={monthlyByProvinceChart}
          monthlyByProvinceCombinedChart={monthlyByProvinceCombinedChart}
          provinceChartCombinedView={provinceChartCombinedView}
          onProvinceChartCombinedViewChange={setProvinceChartCombinedView}
          quarterlyChart={quarterlyChart}
          isMonthlyByProductLoading={isMonthlyProductChartLoading}
          isMonthlyByRetailerLoading={isMonthlyByRetailerLoading}
          isMonthlyByProvinceLoading={isMonthlyByProvinceLoading}
          isQuarterlyLoading={isQuarterlyLoading}
          monthlyByProductError={monthlyByProductError?.message}
          monthlyByRetailerError={monthlyByRetailerError?.message}
          monthlyByProvinceError={monthlyByProvinceError?.message}
          quarterlyError={quarterlyError?.message}
          quarterlyLookbackYears={quarterlyLookbackYears}
          availableQuarterlyLookbackYears={availableQuarterlyLookbackYears}
          onQuarterlyLookbackChange={setQuarterlyLookbackYears}
          onMonthlyProductFacetClick={handleMonthlyProductLineClick}
          accordionExpanded={pricingChartsAccordionExpanded}
          onAccordionExpandedChange={setPricingChartsAccordionExpanded}
          onAccordionEntered={accordionCaptureWaiters.notifyPricingChartsAccordionEntered}
          chartRefMonthlyProduct={setPricingChartRefForKey("monthly-product")}
          monthlyByProductChartGranularity={productChartGranularity}
          chartRefMonthlyRetailer={setPricingChartRefForKey("monthly-retailer")}
          chartRefMonthlyProvince={setPricingChartRefForKey("monthly-province")}
          chartRefQuarterly={setPricingChartRefForKey("quarterly")}
        />

          {pricingPrintError ? (
            <InsightsErrorState
              message={pricingPrintError}
              fallbackMessage="Something went wrong while opening the print dialog."
            />
          ) : null}

          {anyPricingFetchError ? (
            <InsightsErrorState
              message={anyPricingFetchError}
              fallbackMessage="Something went wrong while loading pricing data."
            />
          ) : null}
          {aggregateDownloadError ? (
            <InsightsErrorState
              message={aggregateDownloadError}
              fallbackMessage="Something went wrong while downloading pricing data."
            />
          ) : null}

          {shouldShowAggregateResults ? (
            <Box sx={{ p: 2, ...statisticCardContainerSx }}>
              <AggregateResultsHeader
                title={AGGREGATE_RESULTS_TITLE}
                subtitle={AGGREGATE_RESULTS_SUBTITLE}
                headerActions={pricingReportAggregateHeaderActions}
              />
              {pricingReportResultsView === "card" ? (
                <PricingReportAggregateCardGrid
                  rows={averageUnitPriceTableRows}
                  coverUrlByRowKey={coverUrlLookupForCardGrid}
                  onCardClick={handleAggregateCardClick}
                  isLoading={aggregateInitialLoading}
                  isRefreshing={
                    aggregatePageRefreshing ||
                    (Boolean(aggregateCoverSwrKey) && isAggregateCoversLoading)
                  }
                  serverPagination={aggregateServerPagination}
                />
              ) : (
                <ReportTable
                  columns={AGGREGATE_TABLE_COLUMNS}
                  rows={averageUnitPriceTableRows}
                  onCountClick={handleAggregateCountClick}
                  onProductClick={handleSelectProductById}
                  onProvinceClick={handleSelectProvinceFromDrilldown}
                  onRetailerClick={handleSelectRetailerFromDrilldown}
                  isLoading={aggregateInitialLoading}
                  isRefreshing={aggregatePageRefreshing}
                  serverPagination={aggregateServerPagination}
                  sortBy={aggregateTableSortUi.columnKey}
                  sortDirection={aggregateTableSortUi.direction}
                  onSortChange={handleAggregateTableSortChange}
                />
              )}
            </Box>
          ) : null}
          </Stack>
        </Box>
      </Stack>
    </LocalizationProvider>
  );

  return (
    <Box sx={{ py: 0 }}>
      {pricingReportsMainContent}
      {pricingReportPrintSnapshot
        ? createPortal(
            <PricingReportPrintView snapshot={pricingReportPrintSnapshot} />,
            document.body
          )
        : null}
      <PriceHistoryBreakdownDialog
        open={breakdownTableDialogOpen}
        aggregateRow={selectedAggregateDrilldown}
        rows={breakdownRows || []}
        isLoading={breakdownInitialLoading}
        error={breakdownError}
        onClose={handleCloseAggregateBreakdown}
        onEditRecord={(item) => handleBreakdownTableViewRecord(item.id)}
      />
      <PriceHistoryBreakdownGalleryDialog
        open={breakdownGalleryDialogOpen}
        aggregateRow={selectedAggregateDrilldown}
        rows={breakdownRows || []}
        isLoading={breakdownInitialLoading}
        error={breakdownError}
        onClose={handleCloseAggregateBreakdown}
        focusedRecordId={galleryFocusedRecordId}
        initialPanelMode={galleryInitialPanelMode}
        productFilterChoices={productFilterChoices}
        retailerChoices={retailerChoices}
        provinceChoices={provinceChoices}
        unitChoices={breakdownUnitChoices}
        formatProductOptionLabel={formatProductOptionLabel}
        isPublishedProductsLoading={!publishedProducts && !publishedProductsError}
        isLoadingFilterOptions={isLoadingFilterOptions}
        resolveProvinceForApi={resolveProvinceForApi}
        onSaveRecord={handlePriceHistoryRecordSave}
        onVoidRecord={handlePriceHistoryRecordVoid}
      />
    </Box>
  );
}

export default PricingReportsPanel;
